import { createRouter, createWebHistory } from "vue-router";
import VueRouteMiddleware from "vue-route-middleware";
import { useAuthStore } from "@/stores/auth";

import auth from "@/middleware/auth";

const routes = [
  {
    path: "/",
    name: "index",
    redirect: "/login",
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/pages/AuthLogin.vue"),
  },
  {
    path: "/signup",
    name: "signup",
    component: () => import("@/views/pages/Signup.vue"),
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: () => import("@/views/pages/ForgotPassword.vue"),
  },
  {
    path: "/create-password",
    name: "create-password",
    component: () => import("@/views/pages/CreatePassword.vue"),
  },
  {
    path: "/reset-password",
    name: "reset-password",
    component: () => import("@/views/pages/ResetPassword.vue"),
  },
  {
    path: "/two-factor",
    name: "two-factor",
    meta: {
      requiresAuth: true,
      middleware: [auth], // Apply the auth middleware to the two-factor route
    },
    component: () => import("@/views/pages/TwoFactorVerification.vue"),
  },
  {
    path: "/saml-validate",
    name: "saml-validate",
    component: () => import("@/views/pages/SamlValidate.vue"),
  },
  {
    path: "/main",
    name: "main",
    component: () => import("@/views/MainPage.vue"),
    meta: {
      middleware: [auth],
    },
    redirect: "/dashboard",
    children: [
      {
        path: "/dashboard",
        name: "dashboard",
        component: () => import("@/views/pages/DashBoard.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/validate',
        alias: '/validate/',
        name: 'validate',
        component: () => import("@/views/pages/ValidationPage.vue"),
        meta: {
          requiresAuth: true,
          middleware: [auth],
        },
      },
      {
        path: "/organisations",
        name: "organisations-base",
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore();
          const isSuperAdmin = authStore.isSuperAdmin || authStore.user?.is_super_admin;
          if (isSuperAdmin) {
            if (authStore.organisations && authStore.organisations.length > 0) {
              next(`/organisations/${authStore.organisations[0].id}`);
            } else {
              // Handle case where organisations are not loaded yet
              authStore.getOrganisations().then(() => {
                const orgId = authStore.organisations && authStore.organisations.length > 0 ? authStore.organisations[0].id : 1;
                next(`/organisations/${orgId}`);
              }).catch(() => {
                next(`/organisations/1`);
              });
            }
          } else {
            const orgId = authStore.user?.organisation_access?.id || 1;
            next(`/organisations/${orgId}`);
          }
        },
      },
       {
        path: "/organisations/:id/settings",
        name: "organisations-settings", // Changed name to avoid conflict with '/organisations/:id'
        props: true, // This allows you to pass the 'id' as a prop to your component
        component: () => import("@/views/pages/OrganisationSettings.vue"),
        meta: {
          requiresAuth: true,
        },
       
      },
      {
        path: "/organisations/:id",
        name: "organisations",
        props: true, // This allows you to pass the 'id' as a prop to your component
        component: () => import("@/views/pages/OrganisationSettings.vue"),
        meta: {
          requiresAuth: true,
        },
        children: [
          {
            path: ":userId",
            name: "user-settings-view",
            component: () =>
              import("@/components/SideBarDetails.vue"),
            props: true,
            meta: {
              requiresAuth: true,
            },
          },
        ],
      },
      {
        path: "/products",
        name: "products",
        component: () => import("@/views/pages/ProductsPage.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "/product-settings/:id",
        name: "product-settings",
        component: () => import("@/views/pages/ProductSettings.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "/platform-settings",
        name: "platform-settings",
        component: () => import("@/views/pages/PlatformSettings.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "/invoice-management",
        name: "invoice-management",
        component: () => import("@/views/pages/InvoiceManagement.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "/reporting/users",
        name: "reporting-users",
        component: () => import("@/views/pages/ReportingUsers.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "/reporting/invoices",
        name: "reporting-invoices",
        component: () => import("@/views/pages/ReportingInvoices.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "/reporting/organisations",
        name: "reporting-organisations",
        component: () => import("@/views/pages/ReportingOrganisations.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "/user-settings",
        name: "user-settings",
        component: () => import("@/views/pages/UserSettings.vue"),
        meta: {
          requiresAuth: true,
           middleware: [auth], // Apply the auth middleware to the two-factor route
        },
      },
      {
        path: "/documentations",
        name: "documentations",
        component: () => import("@/views/pages/DocumentationsPage.vue"),
        meta: {
          requiresAuth: true,
        },
      },
    ],
  },
  // Catch-all route for 404 Not Found
  {
    path: "/:catchAll(.*)", // This will match any path that hasn't been matched yet
    name: "NotFound",
    component: () => import("@/views/pages/NotFoundPage.vue"), // You'll need to create this component
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(VueRouteMiddleware({ auth }));

router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some(
    (record) => record.meta && record.meta.requiresAuth
  );
  if (requiresAuth && (!to.meta || !to.meta.middleware)) {
    return auth(to, from, next);
  }
  next();
});

if (typeof window !== "undefined") {
  const checkAuthOnBrowserNavigation = () => {
    try {
      const authStore = useAuthStore();
      authStore.setJwtPayloadData();
      const currentRoute = router.currentRoute.value;
      const requiresAuth = currentRoute.matched.some(
        (record) => record.meta && record.meta.requiresAuth
      );
      if (requiresAuth) {
        const getCookie = (name) => {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts.length === 2) return parts.pop().split(";").shift();
          return null;
        };
        const token = authStore.user?.access_token || getCookie("authToken");
        if (!token || authStore.isTokenExpired || !authStore.isUserAuthenticated) {
          authStore.resetState();
          router.replace("/login");
        }
      }
    } catch (e) {
      console.error("Navigation auth check error:", e);
    }
  };

  window.addEventListener("pageshow", checkAuthOnBrowserNavigation);
  window.addEventListener("popstate", checkAuthOnBrowserNavigation);
}

export default router;
